import type { FC, ReactElement } from "react"
import { useRef, useState } from "react"

import type { IconType } from "@productlane/ui"
import { cn } from "@productlane/lib/src/cn"
import { Button } from "@productlane/ui"

import { toast } from "@/components/toast"
import { useRole } from "@/lib/useRole"

interface Props {
	defaultValue: number | string | null
	icon?: IconType
	name?: string
	onChange: (input: number | string) => void
	onValidate?: (value: number | string) => string
	placeholder?: string
	postfix?: string
	renderValue?: (value: string) => ReactElement
	className?: string
}

export const InputChange: FC<Props> = ({
	defaultValue,
	icon: Icon,
	name,
	onChange,
	onValidate,
	placeholder,
	postfix,
	renderValue,
	className,
}) => {
	const { role } = useRole()
	const [isEditing, setIsEditing] = useState(false)
	const [draft, setDraft] = useState("")
	const skipCommit = useRef(false)

	const value = defaultValue == null ? "" : String(defaultValue)
	const trimmedDraft = draft.trim()
	const draftError = trimmedDraft === "" ? "" : onValidate?.(trimmedDraft)

	const startEditing = () => {
		if (role !== "ADMIN") return
		setDraft(value)
		setIsEditing(true)
	}

	const commit = () => {
		setIsEditing(false)
		if (trimmedDraft === value || (trimmedDraft === "" && value === "")) return

		const error = onValidate?.(trimmedDraft)
		if (error) {
			toast.info({ title: error, icon: Icon })
			return
		}

		onChange(trimmedDraft)
	}

	return (
		<div
			className={cn("flex h-7 items-center text-sm text-secondary", className)}
		>
			{isEditing ? (
				// Same box as the ghost Button below, so the row holds still when editing starts.
				<label className="flex h-7 w-full items-center gap-2 rounded-full bg-gray-150 px-3 text-xs font-medium dark:bg-gray-800">
					{!!Icon && (
						<Icon
							className={cn(
								"flex-none",
								value !== ""
									? "text-secondary dark:text-primary"
									: "text-tertiary",
							)}
						/>
					)}
					<input
						autoFocus
						aria-label={name}
						aria-invalid={!!draftError}
						inputMode="decimal"
						value={draft}
						placeholder={placeholder}
						onChange={(event) => setDraft(event.target.value)}
						onFocus={(event) => event.currentTarget.select()}
						onKeyDown={(event) => {
							if (event.key === "Enter") {
								event.currentTarget.blur()
							} else if (event.key === "Escape") {
								event.stopPropagation()
								skipCommit.current = true
								event.currentTarget.blur()
							}
						}}
						onBlur={() => {
							if (skipCommit.current) {
								skipCommit.current = false
								setIsEditing(false)
								return
							}
							commit()
						}}
						style={
							postfix
								? {
										width: `${Math.max(draft.length, placeholder?.length ?? 0, 1)}ch`,
									}
								: undefined
						}
						className={cn(
							"h-full min-w-0 bg-transparent p-0 text-xs font-medium tabular-nums text-primary outline-none placeholder:text-tertiary",
							{ "flex-1": !postfix, "text-red-500": !!draftError },
						)}
					/>
					{!!postfix && (
						<span className="flex-none text-tertiary">{postfix}</span>
					)}
				</label>
			) : (
				<Button
					onClick={startEditing}
					className={cn(
						"w-full max-w-none justify-start text-xs hover:bg-gray-150 dark:hover:bg-gray-800",
						{
							"!text-tertiary": value === "",
							"!text-primary": value !== "",
						},
					)}
					variant="ghost"
					{...(Icon
						? {
								Icon: () => (
									<Icon
										className={cn(
											"flex-none",
											value !== ""
												? "text-secondary dark:text-primary"
												: "text-tertiary",
										)}
									/>
								),
							}
						: {})}
				>
					{value === ""
						? renderValue
							? renderValue("0")
							: `Set ${name?.toLowerCase()}`
						: renderValue
							? renderValue(value)
							: value}
				</Button>
			)}
		</div>
	)
}
